/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export type MuensterTimeSeriesDate = Date | number | string;

interface DateParts {
    day: number;
    month: number;
    year: number;
}

export class MuensterTimeSeriesUrl {
    private static readonly MUENSTER_SENSOR_PATTERN = /^Mue\.(\d{4})\.([A-Za-z]+)_(.+)$/;
    private static readonly GERMAN_DATE_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T].*)?$/;
    private static readonly ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T].*)?$/;
    private static readonly US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T].*)?$/;

    private readonly sensorName: string;

    constructor(sensorName: string) {
        this.sensorName = sensorName;
    }

    getUrl(date: MuensterTimeSeriesDate): string | null {
        const eviewSensorName = MuensterTimeSeriesUrl.getEviewSensorName(this.sensorName);
        const dateParts = MuensterTimeSeriesUrl.getDateParts(date);

        if (!eviewSensorName || !dateParts) {
            return null;
        }

        return `https://www.eview.de/e1/p3Export.php?frame=StadtMS&p=${eviewSensorName.system};${eviewSensorName.sensor};dg1;t${MuensterTimeSeriesUrl.formatGermanDate(dateParts)}`;
    }

    private static createDateParts(year: number, month: number, day: number): DateParts | null {
        const date = new Date(year, month - 1, day);

        if (
            date.getFullYear() !== year
            || date.getMonth() !== month - 1
            || date.getDate() !== day
        ) {
            return null;
        }

        return { day, month, year };
    }

    private static getDateParts(date: MuensterTimeSeriesDate): DateParts | null {
        if (date instanceof Date) {
            if (Number.isNaN(date.getTime())) {
                return null;
            }

            return {
                day: date.getDate(),
                month: date.getMonth() + 1,
                year: date.getFullYear(),
            };
        }

        if (typeof date === 'number') {
            return MuensterTimeSeriesUrl.getDateParts(new Date(date));
        }

        const normalizedDate = date.trim();
        const germanDate = MuensterTimeSeriesUrl.GERMAN_DATE_PATTERN.exec(normalizedDate);
        const isoDate = MuensterTimeSeriesUrl.ISO_DATE_PATTERN.exec(normalizedDate);
        const usDate = MuensterTimeSeriesUrl.US_DATE_PATTERN.exec(normalizedDate);

        if (germanDate) {
            return MuensterTimeSeriesUrl.createDateParts(
                Number(germanDate[3]),
                Number(germanDate[2]),
                Number(germanDate[1]),
            );
        }

        if (isoDate) {
            return MuensterTimeSeriesUrl.createDateParts(
                Number(isoDate[1]),
                Number(isoDate[2]),
                Number(isoDate[3]),
            );
        }

        if (usDate) {
            return MuensterTimeSeriesUrl.createDateParts(
                Number(usDate[3]),
                Number(usDate[1]),
                Number(usDate[2]),
            );
        }

        return null;
    }

    private static formatGermanDate(dateParts: DateParts): string {
        return `${dateParts.day}.${dateParts.month}.${dateParts.year}`;
    }

    private static getEviewSensorName(sensorName: string): { sensor: string; system: string } | null {
        const sensorMatch = MuensterTimeSeriesUrl.MUENSTER_SENSOR_PATTERN.exec(sensorName.trim());

        if (!sensorMatch) {
            return null;
        }

        const [, system, prefix, identifier] = sensorMatch;
        const eviewSensor = `${prefix}_${identifier}`
            .replaceAll('_', '~')
            .replaceAll(/m(?=[A-Za-z])/g, '-')
            .replaceAll(/p(?=[A-Za-z])/g, '+');

        return {
            sensor: eviewSensor,
            system,
        };
    }
}
